import { useNavigate, useLocation } from "react-router-dom";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import DataService from "../auth/Service";
import { useUser } from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import { toast } from "sonner";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "../ui/sidebar";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import {
  LayoutDashboard,
  MessageSquare,
  BellRing,
  CreditCard,
  User,
  LogOut,
  Sun,
  Moon,
  ChevronUp,
  HistoryIcon,
} from "lucide-react";

import peppaLogo from "../../assets/icons/peppa_logo_blue.svg";

const NAV_ITEMS = [
  { label: "Tableau de bord", icon: LayoutDashboard, path: RouterPath.DASHBOARD },
  { label: "Veilles actives", icon: BellRing, path: RouterPath.ALERTS },
  { label: "Historique des veilles", icon: HistoryIcon, path: RouterPath.ALERTS_HISTORY },
  // { label: "Abonnement", icon: CreditCard, path: RouterPath.BILLING },
];

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, resetUserInfo } = useUser();
  const { isDark, toggleTheme } = useTheme();

  const displayName = user
    ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.email
    : "Utilisateur";

  const handleLogout = async () => {
    try {
      await DataService.logout();
      resetUserInfo();
      navigate(RouterPath.LOGIN);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la déconnexion.");
    }
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <Sidebar collapsible="icon">

      {/* ── Logo ── */}
      <SidebarHeader className="p-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <img src={peppaLogo} alt="PeppaCorp" className="h-6 w-auto m-2 object-contain" />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarSeparator />

      {/* ── Navigation ── */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.label}>
                  <SidebarMenuButton
                    onClick={() => navigate(item.path)}
                    isActive={isActive(item.path)}
                    tooltip={item.label}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* ── Footer ── */}
      <SidebarFooter className="p-3 space-y-1">
        <SidebarSeparator className="mb-2" />

        {/* Toggle thème — un simple div, pas de button imbriqué */}
        <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-sidebar-foreground">
          <Sun className="h-4 w-4 text-yellow-500 shrink-0" />
          <label className="flex items-center gap-2 select-none">
            <div
              role="switch"
              aria-checked={isDark}
              onClick={toggleTheme}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isDark ? "bg-primary" : "bg-input"
                }`}
            >
              <span
                className={`pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform duration-200 ${isDark ? "translate-x-4" : "translate-x-0"
                  }`}
              />
            </div>
          </label>
          <Moon className="h-4 w-4 text-blue-400 shrink-0" />
        </div>

        {/* User menu — clic sur toute la ligne ouvre le dropdown */}
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  tooltip={displayName}
                  className="w-full"
                >
                  <User className="h-4 w-4 shrink-0" />
                  <span className="truncate flex-1 text-left">{displayName}</span>
                  <ChevronUp className="h-4 w-4 shrink-0 ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-52">
                <DropdownMenuItem onClick={() => navigate(RouterPath.PROFILE)}>
                  <SidebarMenuButton className="w-full p-0 border-0">
                    <User className="mr-2 h-4 w-4" />
                    Mon profil
                  </SidebarMenuButton>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(RouterPath.BILLING)}>
                  <SidebarMenuButton className="w-full p-0 border-0">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Abonnement
                  </SidebarMenuButton>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <SidebarMenuButton className="w-full p-0 border-0">
                    <LogOut className="mr-2 h-4 w-4" />
                    Se déconnecter
                  </SidebarMenuButton>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>

      </SidebarFooter>
    </Sidebar>
  );
}